import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { Observable, Subscription } from 'rxjs';
import { ErrorWarningDialogComponent } from '../error-warning-dialog/error-warning-dialog.component';
import { AuthService } from '../shared/auth.service';
import { FishingEvent } from '../shared/fishing-event.model';
import { LocalStorageService } from '../shared/local-storage.service';
import { OperatorUser } from '../shared/operator-user.model';
import { SetErrorBlock } from '../shared/set-error-block.model';
import { lookupItemValidator, TripValidator } from '../shared/trip-validator.model';
import { Trip } from '../shared/trip.model';
import { FormControl, FormGroup } from '@angular/forms';
import { LookupItem } from '../shared/lookup-item.model';
import { map, startWith } from 'rxjs/operators';
import { LookupDataService } from '../shared/lookup-data.service';
import { formatDate } from '@angular/common';
import { DateTimePickerComponent } from '../date-time-picker/date-time-picker.component';
import { TrawlSet } from '../shared/trawl-set.model';
import { TrawlTrip } from '../shared/trawl-trip.model';
import { ToastrService } from 'ngx-toastr';
import { Insights } from '../shared/insights.service';


enum Fishery {Groundfish, HMS, Trawl};

/**
 * The main menu, which displays a table view of the set list
 * From here we can go to the, completed delivery list, current delivery, the settings, or the profile page.
 * All these are their own components
 */
@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.css'],
    standalone: false
})
export class MenuComponent implements OnInit {

  user: OperatorUser = LocalStorageService.current_user;
  userSubscription!: Subscription;
  syncInProgress: boolean = LocalStorageService.sync_in_progress;
  editedLogSubscription!: Subscription;
  editedTrawlLogSubscription!: Subscription;
  syncProgSub!: Subscription;
  editedDeliveryId: string = LocalStorageService.edited_logbook.GF.logbookId ?? LocalStorageService.edited_logbook.HMS.logbookId;
  editedTrawlDeliveryId: string = LocalStorageService.edited_trawlLogbook.logbookId;

  @ViewChild('currentDeliveryMenuTrigger') currentDeliveryMenuTrigger!: MatMenuTrigger;
  @ViewChild('addSetMenuTrigger') addSetMenuTrigger!: MatMenuTrigger;
  panelOpen: boolean = false;

  setSubscription!: Subscription;
  setErrors: SetErrorBlock[] = [];
  isInit: boolean = true;

  groundfishSets: FishingEvent[] = [];
  hmsSets: FishingEvent[] = [];
  trawlSets: TrawlSet[] = [];
  selectedFishery: Fishery = Fishery.Groundfish;

  landingForm: FormGroup = new FormGroup({
    departureDate: new FormControl(), //Default to earliest set date
    departurePort: new FormControl<LookupItem>(new LookupItem(''), lookupItemValidator()),
    isEMTrip: new FormControl<boolean>(false) //Must select before starting trip???
  });

  hmsPortOptions: LookupItem[] = [];
  trawlPortOptions: LookupItem[] = [];
  filteredDeparturePorts!: Observable<LookupItem[]>;
  portSubscription!: Subscription;

  formUpdateSubscription!: Subscription;

  static set_num = 1;

  constructor(
    public router: Router,
    public lookupService: LookupDataService,
    public route: ActivatedRoute,
    public dialog: MatDialog,
    private auth: AuthService,
    private toatr: ToastrService,
    private insights: Insights,
    private onlineStatusService: OnlineStatusService) {

  }

  ngOnInit(): void {

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        // Scroll to top on navigation end
        window.scrollTo(0, 0);
      }
    });

    let fromFishery = this.route.snapshot.queryParams.fishery ?? 0;
    this.selectedFishery = fromFishery;

    this.syncProgSub = LocalStorageService.sync_in_progress$.subscribe(val => this.syncInProgress = val);
    this.userSubscription = LocalStorageService.current_user$.subscribe(user => {
      this.user = user; 
      if (user.groundfishFishery && user.hmsFishery) this.selectedFishery = fromFishery;
      else if (user.groundfishFishery) this.selectedFishery = Fishery.Groundfish;
      else if (user.hmsFishery) this.selectedFishery = Fishery.HMS;
      else if (user.trawlFishery) this.selectedFishery = Fishery.Trawl;
    });

    this.editedLogSubscription = LocalStorageService.edited_logbook$.subscribe(val => this.editedDeliveryId = val.GF.logbookId ?? val.HMS.logbookId);
    this.editedTrawlLogSubscription = LocalStorageService.edited_trawlLogbook$.subscribe(val => this.editedTrawlDeliveryId = val.logbookId)

    this.hmsPortOptions = this.lookupService.getHmsPorts();
    this.portSubscription = this.lookupService.getPortsObservable().subscribe((value) => this.trawlPortOptions = value);   

    this.filteredDeparturePorts = this.landingForm.get('departurePort')!.valueChanges.pipe(
      startWith(''),
      map(value => (this.selectedFishery == Fishery.HMS ? this.hmsPortOptions : this.trawlPortOptions).filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
    );

    if (localStorage.getItem('rtokenexpiry') == 'true') {
      localStorage.setItem('rtokenexpiry', 'false');
      this.router.navigate(['delivery_detail', 0, -1], { queryParams: { previous: 'menu' } });
    }


    //Fetch pending sets
    LocalStorageService.storage_promise.then(_ => {
      if (this.user.trawlFishery) {
        this.initializeData(LocalStorageService.user_trawlTrip);
        this.setSubscription = LocalStorageService.user_trawlTrip$.subscribe(val => this.initializeData(val));
      } else {
        this.initializeData(LocalStorageService.user_trip);
        this.setSubscription = LocalStorageService.user_trip$.subscribe(val => this.initializeData(val));
      }
    });

    this.formUpdateSubscription = this.landingForm.valueChanges.subscribe(value => {
      if (this.selectedFishery == Fishery.HMS) {
        LocalStorageService.user_trip.hmsDelivery.departureDate = value.departureDate;
        LocalStorageService.user_trip.hmsDelivery.departurePort = value.departurePort;
        LocalStorageService.updateLanding(LocalStorageService.user_trip.hmsDelivery).then(res => {
          if (!res.success) {
            this.toatr.error("Error updating delivery", "Error");
            this.insights.trackException(res.error);
          }
        });
      } else if (this.selectedFishery == Fishery.Trawl) {
        LocalStorageService.user_trawlTrip.trawlDelivery.departureDate = value.departureDate;
        LocalStorageService.user_trawlTrip.trawlDelivery.departurePort = value.departurePort;
        LocalStorageService.user_trawlTrip.trawlDelivery.isEMTrip = value.isEMTrip;
        LocalStorageService.updateLanding(LocalStorageService.user_trawlTrip.trawlDelivery, 'trawl').then(res => {
          if (!res.success) {
            this.toatr.error("Error updating delivery", "Error");
            this.insights.trackException(res.error);
          }
        })
      }
      console.log("Update landing")    
    });
    
    if (!this.user.groundfishFishery && this.user.hmsFishery) this.selectedFishery = Fishery.HMS;
    if (this.user.trawlFishery) this.selectedFishery = Fishery.Trawl;
  }


  initializeData(trip: Trip | TrawlTrip) {
    if (trip instanceof TrawlTrip) {
      this.trawlSets = trip.sets.sort((a, b) =>
        (a.startDate ? new Date(a.startDate).getTime() : Number.MIN_SAFE_INTEGER) -
        (b.startDate ? new Date(b.startDate).getTime() : Number.MIN_SAFE_INTEGER));
      this.setErrors = TripValidator.trawlErrorWarningDialogContent();
      this.landingForm.patchValue({
        departureDate: trip.trawlDelivery.departureDate ? formatDate(trip.trawlDelivery.departureDate, 'fullDate', 'en-US') : '',
        departurePort: trip.trawlDelivery.departurePort ?? '',
        isEMTrip: trip.trawlDelivery.isEMTrip
      }, { emitEvent: false });
    }

    else if (trip instanceof Trip) {
      this.groundfishSets = trip.sets.filter(s => s.fishery == 'Groundfish');
      this.hmsSets = trip.sets.filter(s => s.fishery == 'HMS');
      this.hmsSets.sort((a, b) =>
        (a.startDate ? new Date(a.startDate).getTime() : Number.MIN_SAFE_INTEGER) -
        (b.startDate ? new Date(b.startDate).getTime() : Number.MIN_SAFE_INTEGER));
      this.setErrors = TripValidator.errorWarningDialogContent();
      this.panelOpen = trip.hmsDelivery.isNewDelivery;

      this.landingForm.patchValue({
        departureDate: trip.hmsDelivery.departureDate ? formatDate(trip.hmsDelivery.departureDate, 'fullDate', 'en-US') : '',
        departurePort: trip.hmsDelivery.departurePort ?? ''
      }, { emitEvent: false });
    }
  }


  editDateTime() {
    let dateInput = this.landingForm.get('departureDate')?.value == '' ? new Date() : new Date(this.landingForm.get('departureDate')?.value);
    let minArrivalDate = this.selectedFishery == Fishery.HMS ? this.hmsSets.filter(st => st.fishery == 'HMS').map(s => new Date(s.startDate)).filter(d => d && !Number.isNaN(d.getTime()))
    .reduce((curr, acc) => new Date(curr).toISOString() > new Date(acc).toISOString() ? curr : acc, dateInput)
    :
    LocalStorageService.user_trawlTrip.trawlDelivery.returnDate ?? new Date();

    const dialogRef = this.dialog.open(DateTimePickerComponent, {
      width: '500px',
      data: { date: dateInput, minDate: minArrivalDate, isDelivery: true, hmsField: 'departureDate' },
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) this.landingForm.get('departureDate')?.setValue(formatDate(result, 'fullDate', 'en-US'));
    });
  }

  /**
  * Navigates the user to the set for a given row in the table when clicked
  */
  clickRow(set: any) {
    if (set instanceof TrawlSet) {
      let ind = LocalStorageService.user_trawlTrip.sets.findIndex(s => s.fishingEventID == set.fishingEventID);
      this.router.navigate(['set_detail_trawl', ind, false]);
    }
    else {
      let ind = LocalStorageService.user_trip.sets.findIndex(s => s.fishingEventID == set.fishingEventID);
      if (set.fishery == "Groundfish") this.router.navigate(['set_detail', ind, false]);
      else if (set.fishery == "HMS") this.router.navigate(['set_detail_hms', ind, false]);
      else console.log("Error: Fishery type not found");
    }
  }


  /**
* Handles whether the set menu should open when add set button is clicked
*/
  handleAddSetClick() {
    if (this.user.groundfishFishery && this.user.hmsFishery) {
      if (this.selectedFishery == Fishery.Groundfish) this.addNewSetGroundfish();
      if (this.selectedFishery == Fishery.HMS) this.addNewSetHMS();
    }
    else if (this.user.groundfishFishery) this.addNewSetGroundfish();
    else if (this.user.hmsFishery) this.addNewSetHMS();
    else if (this.user.trawlFishery) this.addNewSetTrawl();
    else this.addNewSetGroundfish();
  }

  /**
   * Create new set, generate unique id for it, store that id in sets_list, store new set as new document
   */
  addNewSetGroundfish() {
    LocalStorageService.storeFishingEvent(new FishingEvent('', "Groundfish"))
    .then(res => {
      if (res.success) this.router.navigate(['set_detail', LocalStorageService.user_trip.sets.findIndex(s => s.fishingEventID == res.data), true]);
      else {
        this.toatr.error("Error creating new set", "Error");
        this.insights.trackException(res.error);
      }
  });  
  }


  /**
 * Create new set, generate unique id for it, store that id in sets_list, store new set as new document
 */
  addNewSetHMS() {
    LocalStorageService.storeFishingEvent(new FishingEvent('', "HMS"))
    .then(res => {
      if (res.success) this.router.navigate(['set_detail_hms', LocalStorageService.user_trip.sets.findIndex(s => s.fishingEventID == res.data), true]);
      else {
        this.toatr.error("Error creating new set", "Error");
        this.insights.trackException(res.error);
      }
    });
  }

    /**
 * Create new set, generate unique id for it, store that id in sets_list, store new set as new document
 */
  addNewSetTrawl() {
    LocalStorageService.storeFishingEvent(new TrawlSet(''), "trawl")
    .then(res => { 
      if (res.success) this.router.navigate(['set_detail_trawl', LocalStorageService.user_trawlTrip.sets.findIndex(s => s.fishingEventID == res.data), true]);
      else {
        this.toatr.error("Error creating new set", "Error");
        this.insights.trackException(res.error);
      }
  });
  }


  /**
* Handles whether the menu should open when delivery button is clicked
*/
  handleDeliveryClick() {
    if (this.user.groundfishFishery && this.user.hmsFishery) {
      if (this.selectedFishery == Fishery.Groundfish) this.goToDeliveryGroundfish();
      if (this.selectedFishery == Fishery.HMS) this.goToDeliveryHMS();
    }
    else if (this.user.hmsFishery) this.goToDeliveryHMS();
    else if (this.user.groundfishFishery) this.goToDeliveryGroundfish();
    else if (this.user.trawlFishery) this.goToDeliveryTrawl();
  }


  /**
* Go to groundfish delivery
*/
  goToDeliveryGroundfish() {
    this.router.navigate(['delivery_detail', 0, 0]);
  }

/**
* Go to HMS delivery
*/
  goToDeliveryHMS() {
    this.router.navigate(['hms_delivery_detail', 0, 0])
  }


/**
* Go to Trawl delivery
*/
  goToDeliveryTrawl() {
    this.router.navigate(['trawl_delivery', 0, 0]);
  }


/**
* Go to HMS or Trawl draft page
*/
  handleDraftClick() {
    if (this.user.hmsFishery) this.router.navigate(['hms_drafts']);
    else if (this.user.trawlFishery) this.router.navigate(['trawl_drafts']);
  }


  /**
  * Get errors from {@link TripValidator.errorWarningDialogContent}
  */
  generateSetErrors() {
    let h = (Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0) - 10).toString() + 'px';
    let w = (Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) - 10).toString() + 'px';
    //Launch dialog with collected results
    const dialogRef = this.dialog.open(ErrorWarningDialogComponent, {
      autoFocus: false,
      width: w,
      height: h,
      data: { errors: this.setErrors, submission: false },
    });

    //Clicking on a block will probably have to take user to clicked set
    dialogRef.afterClosed().subscribe(result => {
      if (result != undefined) {
        if (this.selectedFishery == Fishery.Trawl) {
          if (result == -1) this.router.navigate(['delivery_detail', 0, -1]);
          else this.router.navigate(['set_detail_trawl', result, false]);
        } else {
          if (LocalStorageService.user_trip.sets[result].fishery == "Groundfish") this.router.navigate(['set_detail', result, false]);
          else if (LocalStorageService.user_trip.sets[result].fishery == "HMS") this.router.navigate(['set_detail_hms', result, false]);
          else if (result == -1) this.router.navigate(['delivery_detail', 0, -1]);
        }
        
      }
    });
  }
  /**
* Returns the length of the setErrors array for the current tripIndex.
*/
  get SetErrorsLength(): number {
    return this.setErrors.length ?? 0;
  }

  get AnyErrors(): boolean {
    return this.setErrors.some(se => se.isError);
  };

  /**
   * Set the login_status document, cleanup storage, unset {@link LocalStorageService.current_user}
   */
  logout() {
    LocalStorageService.logged_in = false;
    LocalStorageService.sync_in_progress = false;
    
    LocalStorageService.local_storage.get('login_status').then((doc: any) => {
      doc.user = '';
      doc.isLoggedIn = false;
      LocalStorageService.local_storage.put(doc);
      LocalStorageService.cleanupStorage();
      
    });
    if (this.auth.checkAccessToken().expired || this.onlineStatusService.getStatus() == OnlineStatusType.OFFLINE)
      this.router.navigate(['login']);
    else if (this.onlineStatusService.getStatus() == OnlineStatusType.ONLINE)
      this.auth.postRevokeToken().subscribe(_ => this.router.navigate(['login']));
         

    LocalStorageService.current_user = new OperatorUser();
  }


  ngOnDestroy(): void {
    if (this.syncProgSub) this.syncProgSub.unsubscribe();
    if (this.editedLogSubscription) this.editedLogSubscription.unsubscribe();
    if (this, this.editedTrawlLogSubscription) this.editedTrawlLogSubscription.unsubscribe();
    if (this.setSubscription) this.setSubscription.unsubscribe();
    if (this.portSubscription) this.portSubscription.unsubscribe();
    if (this.formUpdateSubscription) this.formUpdateSubscription.unsubscribe();
    if (this.userSubscription) this.userSubscription.unsubscribe();
  }



  selectGroundfish() {
    this.selectedFishery = 0;
  }


  selectHMS() {
    this.selectedFishery = 1;  
  }
}

